import { convertBRToUSDate, getOnlyNumbers } from "@/utils";
import api from "..";
import { v4 as uuidV4 } from "uuid";

import {
  PaymentUserJourneyRawData,
  PaymentUserJourneyRequestData,
  StepUserJourneyRequestData,
  UserJourneyRawData,
  UserJourneyRequestData,
  UserJourneyReturn,
} from "./types";
import { useEffect, useState } from "react";
import {
  getUuidStorageSession,
  setUuidStorageSession,
} from "@/utils/saleSessionStorage";

const formatUserJourneyRequest = (
  data: UserJourneyRawData
): UserJourneyRequestData => {
  return {
    uuid: data.uuid || getUUID(),
    passo: data.passo,
    plano: data.product?.tim_id || "0",
    fidelidade: data.loyalty,
    cpf: getOnlyNumbers(data.cpf),
    email: data.email,
    msisdn: getOnlyNumbers(data.tel),
    autorizar_receber_contato: data.autorizarContato,
    nome: data.fullName,
    tipo: data.tipo,
    possui_provisorio: data.possuiProvisorio,
    filiacao: data.motherName,
    nascimento: data.birthDate ? convertBRToUSDate(data.birthDate) : null,
    uf: data.address?.stateCode,
    cidade: data.address?.cityName,
    cep: getOnlyNumbers(data.address?.zipCode),
    logradouro: data.address?.street,
    bairro: data.address?.neighborhood,
    numero: data.address?.number,
    complemento: data.address?.complement || null,
    ponto_referencia: data.address?.referencePoint || null,
    sem_numero: data.address?.noNumber || false,
    forma_pagamento: data.paymentType?.value,
    vencimento: data.payment?.debtDueDate,
    utm_campaign: data.utm_campaign,
    utm_medium: data.utm_medium,
    utm_source: data.utm_source,
    retomado: data.retomado,
  };
};

export const sendUserJourney = async (data: UserJourneyRawData) => {
  if (import.meta.env.VITE_SHOULD_SEND_USER_JOURNEY === "true") {
    await api
      .post("/core/jornada-usuario", {
        user_journey: formatUserJourneyRequest(data),
      })
      .catch(() => {
        // do nothing
      });
  }
};

const formatPaymentUserJourneyRequest = (
  data: PaymentUserJourneyRawData
): PaymentUserJourneyRequestData => {
  return {
    uuid: data.uuid || getUUID(),
    passo: data.passo,
    vencimento: data.debtDueDate,
  };
};

export const sendPaymentUserJourney = async (
  data: PaymentUserJourneyRawData
) => {
  if (import.meta.env.VITE_SHOULD_SEND_USER_JOURNEY === "true") {
    await api
      .post("/core/jornada-usuario", {
        user_journey: formatPaymentUserJourneyRequest(data),
      })
      .catch(() => {
        // do nothing
      });
  }
};

export const sendStepUserJourney = async (data: StepUserJourneyRequestData) => {
  if (import.meta.env.VITE_SHOULD_SEND_USER_JOURNEY === "true") {
    await api
      .post("/core/jornada-usuario", {
        user_journey: {
          ...data,
          uuid: data.uuid || getUUID(),
        },
      })
      .catch(() => {
        // do nothing
      });
  }
};

export const useUserJourney = (uuid: string) => {
  const [journey, setJourney] = useState<UserJourneyReturn>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();

  useEffect(() => {
    setIsLoading(true);
    api
      .get<UserJourneyReturn>(`/core/jornada-usuario/${uuid}`)
      .then(({ data }) => setJourney(data))
      .catch((error) => setError(error))
      .finally(() => setIsLoading(false));
  }, [uuid]);

  return { journey, isLoading, error };
};

export const getUUID = () => {
  const uuidStorageSession = getUuidStorageSession();
  if (uuidStorageSession) {
    return JSON.parse(uuidStorageSession);
  }

  const uuid = uuidV4();
  setUuidStorageSession(uuid);
  return uuid;
};
